// context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { setTokens, clearTokens } from "./AxiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import config from "../Configurations/APIConfig";
import apiService from "../Configurations/APIService";
import { jwtDecode } from "jwt-decode";
import { isPlanExpired } from "../Layout/dateUtils";

const AuthContext = createContext();
export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Define special company types that should NOT go to /dashboard
  const specialCompanyTypes = ["VISA", "SERIESFAIR", "PACKAGE", "HOTEL", "SUPPLIER"];

  const getProfileAndUpdateState = async () => {
    try {
      const profile = await apiService.getSingle(config.GETUSERPROFILE);
      setUser({
        id: profile.userId,
        cid: profile.companyId,
        bid: profile.branchId,
        role: profile.roleName,
        name: profile.name,
        email: profile.email,
        phoneNumber: profile.phoneNumber,
        country: profile.country,
        currency: profile.currency,
        logo: profile.logo,
        brandName: profile.brandName,
        companyname: profile.companyName,
        companygstno: profile.companyGSTNo,
        companypancard: profile.companyPanCard,
        companyaddress: profile.companyAddress,
        isWebsite: profile.isWebsite,
        connects: profile.connects,
        isRenewal: profile.isRenewal,
        planType: profile.planType,
        endDate: profile.endDate,
        isChatBot: profile.isChatBot,
        whatsAppNumber: profile.whatsAppNumber,
        isSupplierAPI: profile.isSupplierAPI,
        parentCompanyId: profile.parentCompanyId,
        companyType: profile.companyType,
        walletBalance: profile.walletBalance,
        domesticFlightMarkup: profile.domesticFlightMarkup,
        internationalFlightMarkup: profile.internationalFlightMarkup,
        domesticHotelMarkup: profile.domesticHotelMarkup,
        internationalHotelMarkup: profile.internationalHotelMarkup,
        sightseeingMarkup: profile.sightseeingMarkup,
        busMarkup: profile.busMarkup,
        visaMarkup: profile.visaMarkup
      });
      return profile;
    } catch (err) {
      console.error("Failed to fetch user profile:", err);
      await logout(); // Logout if profile fetch fails
    }
  };

  useEffect(() => {
    let isMounted = true;

    const tryRefresh = async () => {
      const skipPaths = ["/", "/login", "/unauthorized"];
      if (skipPaths.includes(location.pathname)) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        let accessToken = localStorage.getItem("accessToken");
        let refreshToken = localStorage.getItem("refreshToken");

        if (!refreshToken) throw new Error("No refresh token");

        // If access token is expired, refresh it
        if (!accessToken || isTokenExpired(accessToken)) {
          const res = await apiService.post(config.REFRESH, { refreshToken });
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
            res;

          localStorage.setItem("accessToken", newAccessToken);
          localStorage.setItem("refreshToken", newRefreshToken);

          setTokens({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          });

          accessToken = newAccessToken;
        }

        // ✅ Always fetch profile after ensuring valid token
        const profile = await apiService.getSingle(config.GETUSERPROFILE);
        console.log("User Profile:", profile);
        if (isMounted) {
          setUser({
            id: profile.userId,
            cid: profile.companyId,
            bid: profile.branchId,
            role: profile.roleName,
            name: profile.name,
            email: profile.email,
            phoneNumber: profile.phoneNumber,
            country: profile.country,
            currency: profile.currency,
            logo: profile.logo,
            brandName: profile.brandName,
            companyname: profile.companyName,
            companygstno: profile.companyGSTNo,
            companypancard: profile.companyPanCard,
            companyaddress: profile.companyAddress,
            isWebsite: profile.isWebsite,
            connects: profile.connects,
            isRenewal: profile.isRenewal,
            planType: profile.planType,
            endDate: profile.endDate,
            isChatBot: profile.isChatBot,
            whatsAppNumber: profile.whatsAppNumber,
            isSupplierAPI: profile.isSupplierAPI,
            parentCompanyId: profile.parentCompanyId,
            companyType: profile.companyType,
            walletBalance: profile.walletBalance,
            domesticFlightMarkup: profile.domesticFlightMarkup,
            internationalFlightMarkup: profile.internationalFlightMarkup,
            domesticHotelMarkup: profile.domesticHotelMarkup,
            internationalHotelMarkup: profile.internationalHotelMarkup,
            sightseeingMarkup: profile.sightseeingMarkup,
            busMarkup: profile.busMarkup,
            visaMarkup: profile.visaMarkup
          });
        }
      } catch (err) {
        console.error("Token refresh failed:", err);
        if (isMounted) await logout();
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (!localStorage.getItem("refreshToken")) {
      setLoading(false);
      return;
    }
    tryRefresh();
    return () => {
      isMounted = false;
    };
  }, []);

  function isTokenExpired(token) {
    if (!token) return true;
    try {
      const { exp } = jwtDecode(token); // exp is in seconds
      const now = Date.now() / 1000;
      return exp < now; // expired
    } catch (e) {
      return true;
    }
  }

  const login = async ({ accessToken, refreshToken }) => {
    if (!accessToken || !refreshToken) {
      console.error("Missing accessToken or refreshToken");
      return;
    }
    // 🔐 Store both tokens
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    setTokens({
      accessToken: accessToken,
      refreshToken: refreshToken,
    });

    const profile = await apiService.getSingle(config.GETUSERPROFILE);
    console.log("User Profile:", profile);
    setUser({
      id: profile.userId,
      cid: profile.companyId,
      bid: profile.branchId,
      role: profile.roleName,
      name: profile.name,
      email: profile.email,
      phoneNumber: profile.phoneNumber,
      country: profile.country,
      currency: profile.currency,
      logo: profile.logo,
      brandName: profile.brandName,
      companyname: profile.companyName,
      companygstno: profile.companyGSTNo,
      companypancard: profile.companyPanCard,
      companyaddress: profile.companyAddress,
      isWebsite: profile.isWebsite,
      connects: profile.connects,
      isRenewal: profile.isRenewal,
      planType: profile.planType,
      endDate: profile.endDate,
      isChatBot: profile.isChatBot,
      whatsAppNumber: profile.whatsAppNumber,
      isSupplierAPI: profile.isSupplierAPI,
      parentCompanyId: profile.parentCompanyId,
      companyType: profile.companyType,
      walletBalance: profile.walletBalance,
      domesticFlightMarkup: profile.domesticFlightMarkup,
      internationalFlightMarkup: profile.internationalFlightMarkup,
      domesticHotelMarkup: profile.domesticHotelMarkup,
      internationalHotelMarkup: profile.internationalHotelMarkup,
      sightseeingMarkup: profile.sightseeingMarkup,
      busMarkup: profile.busMarkup,
      visaMarkup: profile.visaMarkup
    });

    if (isPlanExpired(profile.endDate)) {
      // If expired, navigate to the subscription plans page and stop.
      navigate("/subscriptionplans");
      return;
    }

    // UPDATED: Check if companyType is in specialCompanyTypes
    if (specialCompanyTypes.includes(profile.companyType)) {
      // Redirect each special company type to their specific dashboard
      switch(profile.companyType) {
        case "SUPPLIER":
          navigate("/holidaydashboard");
          break;
        case "VISA":
          navigate("/visadashboard");
          break;
        case "SERIESFAIR":
          navigate("/seriesfare/dashboard");
          break;
        case "PACKAGE":
          navigate("/holidaydashboard");
          break;
        case "HOTEL":
          navigate("/hoteldashboard");
          break;
        default:
          navigate("/");
      }
    } else {
      // For regular company types, go to /dashboard
      navigate("/dashboard");
    }
  };

  const logout = async () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    clearTokens(); // Assuming this clears any axios headers
    setUser(null);
    navigate("/login", { replace: true });
  };

  const refreshUserProfile = async () => {
    console.log("Refreshing user profile...");
    return await getProfileAndUpdateState();
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, refreshUserProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);